import {
  LOCALE_DEFINITIONS,
  type InstallableLocale,
} from "./preparedLocales";
import { isInstallableLocale, PLATFORM_DEFAULT_LOCALE } from "./localeRegistry";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

export type LocaleAvailability = {
  locale: InstallableLocale;
  language_tag: string;
  direction: "ltr" | "rtl";
  english_label: string;
  installed: boolean;
  enabled: boolean;
  is_default: boolean;
};

export type WorkspaceLocalePreferences = {
  user_locale: InstallableLocale | null;
  organization_default_locale: InstallableLocale | null;
  enabled_locales: InstallableLocale[];
};

export class LocaleApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "LocaleApiError";
  }
}

function cookie(name: string): string | undefined {
  return document.cookie.split(";").map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))?.slice(name.length + 1);
}

function parseLocaleAvailability(value: unknown): LocaleAvailability {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Locale availability response is malformed.");
  }
  const raw = value as Record<string, unknown>;
  if (!isInstallableLocale(raw.locale)) throw new Error("Locale availability contains an unsupported locale.");
  const locale = raw.locale.toLowerCase() as InstallableLocale;
  const definition = LOCALE_DEFINITIONS[locale];
  if (raw.language_tag !== definition.languageTag || raw.direction !== definition.direction) {
    throw new Error("Locale availability metadata does not match the canonical registry.");
  }
  if (typeof raw.english_label !== "string" || !raw.english_label.trim()) {
    throw new Error("Locale availability label is malformed.");
  }
  if (typeof raw.installed !== "boolean" || typeof raw.enabled !== "boolean" || typeof raw.is_default !== "boolean") {
    throw new Error("Locale availability state is malformed.");
  }
  if (raw.is_default && (locale !== PLATFORM_DEFAULT_LOCALE || !raw.installed || !raw.enabled)) {
    throw new Error("Locale availability default state is invalid.");
  }
  if (raw.enabled && !raw.installed) throw new Error("An enabled locale must be installed.");
  return {
    locale,
    language_tag: definition.languageTag,
    direction: definition.direction,
    english_label: raw.english_label,
    installed: raw.installed,
    enabled: raw.enabled,
    is_default: raw.is_default,
  };
}

function parsePreferences(value: unknown): WorkspaceLocalePreferences {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Language preference response is malformed.");
  }
  const raw = value as Record<string, unknown>;
  if (!Array.isArray(raw.enabled_locales)) throw new Error("Enabled locale inventory is malformed.");
  const enabled = raw.enabled_locales.map((locale) => {
    if (!isInstallableLocale(locale)) throw new Error("Enabled locale inventory contains an unsupported locale.");
    return locale.toLowerCase() as InstallableLocale;
  });
  if (!enabled.includes(PLATFORM_DEFAULT_LOCALE)) throw new Error("English must remain enabled.");
  const parseOptional = (candidate: unknown) => {
    if (candidate === null) return null;
    if (!isInstallableLocale(candidate)) throw new Error("Stored language preference is malformed.");
    const locale = candidate.toLowerCase() as InstallableLocale;
    if (!enabled.includes(locale)) throw new Error("Stored language preference is not enabled.");
    return locale;
  };
  return {
    user_locale: parseOptional(raw.user_locale),
    organization_default_locale: parseOptional(raw.organization_default_locale),
    enabled_locales: [...new Set(enabled)],
  };
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const csrf = cookie("kavoosh_csrf");
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.method && init.method !== "GET" && csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { detail?: unknown };
    throw new LocaleApiError(
      response.status,
      typeof body.detail === "string" ? body.detail : "Kavoosh could not update the language preference.",
    );
  }
  return response;
}

export async function listAvailableLocales(): Promise<LocaleAvailability[]> {
  const response = await request("/locales/available");
  const value = await response.json();
  if (!Array.isArray(value)) throw new Error("Locale availability response is malformed.");
  const inventory = value.map(parseLocaleAvailability);
  const english = inventory.find((item) => item.locale === PLATFORM_DEFAULT_LOCALE);
  if (!english?.installed || !english.enabled || !english.is_default) {
    throw new Error("The platform default locale is unavailable.");
  }
  return inventory;
}

export async function getWorkspaceLocalePreferences(): Promise<WorkspaceLocalePreferences> {
  return parsePreferences(await (await request("/locales/preferences")).json());
}

export async function updateUserLocalePreference(locale: InstallableLocale): Promise<WorkspaceLocalePreferences> {
  return parsePreferences(await (await request("/locales/preferences", {
    method: "PUT",
    body: JSON.stringify({ locale }),
  })).json());
}

export async function updateOrganizationDefaultLocale(locale: InstallableLocale): Promise<WorkspaceLocalePreferences> {
  return parsePreferences(await (await request("/locales/organization-default", {
    method: "PUT",
    body: JSON.stringify({ locale }),
  })).json());
}
